import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { Icon } from "@types";
import { create } from "zustand";

interface Content {
  id?: string;
  icon?: {
    icon: Icon;
    color: string;
  };
  content?: string | React.ReactElement;
  extraContent?: string | React.ReactElement;
}

interface NotificationState {
  // Informational notifications: no action required, auto-dismiss on their own.
  show: boolean;
  setShow: (show: boolean) => void;
  content: Content;
  setContent: (content: Content) => void;
  setSuccess: (
    message?: string | React.ReactElement,
    extraContent?: string | React.ReactElement
  ) => void;
  setFailure: (message?: string, is500?: boolean) => void;
  // Action notifications: have a button/next-step (e.g. added to cart), stay
  // open until the user dismisses them.
  actionShow: boolean;
  setActionShow: (show: boolean) => void;
  actionContent: Content;
  setActionContent: (content: Content) => void;
  setActionSuccess: (
    message?: string | React.ReactElement,
    extraContent?: string | React.ReactElement
  ) => void;
}

export const useNotificationStore = create<NotificationState>()((set) => ({
  show: false,
  setShow: (show) => set(() => ({ show })),
  content: {},
  setContent: (content) => set(() => ({ content })),
  setSuccess: (message = "Successfully saved!", extraContent) =>
    set(() => ({
      content: {
        icon: { icon: CheckCircleIcon, color: "emerald" },
        content: message,
        extraContent,
      },
      show: true,
    })),
  setFailure: (message = "Failed to save!", is500 = false) =>
    set(() => ({
      content: {
        icon: {
          icon: is500 ? XCircleIcon : ExclamationCircleIcon,
          color: is500 ? "red" : "orange",
        },
        content: message,
      },
      show: true,
    })),
  actionShow: false,
  setActionShow: (actionShow) => set(() => ({ actionShow })),
  actionContent: {},
  setActionContent: (actionContent) => set(() => ({ actionContent })),
  setActionSuccess: (message = "Successfully saved!", extraContent) =>
    set(() => ({
      actionContent: {
        icon: { icon: CheckCircleIcon, color: "emerald" },
        content: message,
        extraContent,
      },
      actionShow: true,
    })),
}));
